import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { Receiver, Sections, User } from 'src/app/settings/constants';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';
import { ContextualMenuService } from 'src/app/settings/services/contextual-menu.service';
import { ConfirmActionDialogNewComponent } from 'src/app/shared/components/confirm-action-dialog-new-style/confirm-action-dialog.component';
import constants from 'src/app/shared/constants';
import { CreateUserComponent } from './create-user/create-user.component';
import { ReceiverCreateProcessDialogComponent } from '../company-edit-info/company-edit-customize/receiver-create-process-dialog/receiver-create-process-dialog-component';
import { ReceiversService } from 'src/app/shared/services/receivers.service';

@Component({
  selector: 'app-company-edit-users',
  templateUrl: './company-edit-users.component.html',
  styleUrls: ['./company-edit-users.component.scss'],
})
export class CompanyEditUsersComponent implements OnInit {
  @Input() set addReceiver(value: any) {
    if (value) this.createReceiver();
  }

  @Input() set addUser(value: any) {
    if (value) this.createUser();
  }

  @Input() isEdit: boolean;

  receiverMessage: SafeHtml;
  userMessage: SafeHtml;

  showUsers = false;
  showReceivers = false;

  constructor(
    public dialog: MatDialog,
    public authService: AuthService,
    public companiesService: CompaniesService,
    public contextualMenuService: ContextualMenuService,
    private readonly companyUserService: CompanyUserService,
    private readonly translateService: TranslateService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly snackBar: MatSnackBar,
    private readonly receiversService: ReceiversService
  ) {}

  ngOnInit(): void {
    this.matIconRegistry.addSvgIcon(
      'delete_icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/delete.svg')
    );
    this.getTranslation();
  }

  getTranslation = async () => {
    this.receiverMessage = this.domSanitizer.bypassSecurityTrustHtml(
      await lastValueFrom(this.translateService.get('Settings.AddReceiver_desc'))
    );
    this.userMessage = this.domSanitizer.bypassSecurityTrustHtml(
      await lastValueFrom(this.translateService.get('Settings.AddUser_desc'))
    );
  };

  ngOnDestroy(): void {
    this.contextualMenuService.onClose();
  }

  createReceiver() {
    const dialogRef = this.dialog.open(ReceiverCreateProcessDialogComponent, {
      width: '70%',
      maxWidth: '550px',
      data: {
        title: 'Settings.ReceiverCreateProcess',
        description: 'Settings.ReceiverCreateProcess_desc',
        buttonText: 'Shared.Ok',
        company: this.companiesService.currentCompany,
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; newReceiver: Receiver }) => {
      if (result.status === 'confirm') {
        this.onSendCreateReceiver(result.newReceiver);
      }
    });
  }

  onSendCreateReceiver(newReceiver) {
    if (!this.companiesService.currentCompany.receivers) this.companiesService.currentCompany.receivers = [];
    this.companiesService.currentCompany.receivers.push(newReceiver);
  }

  deleteReceiver(receiver) {
    this.companiesService.currentCompany.receivers = this.companiesService.currentCompany.receivers.filter(
      (r) => r.apiKey !== receiver.apiKey
    );
  }

  indexUsers() {
    this.companiesService.currentCompany.users = this.companiesService.currentCompany.users.map((user, index) => {
      return {
        ...user,
        index,
      };
    });
  }

  createUser() {
    const dialogRef = this.dialog.open(CreateUserComponent, {
      width: '40%',
      minWidth: '550px',
      maxWidth: '600px',
      data: {
        users: this.companiesService.currentCompany.users,
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; newUser: User }) => {
      if (result.status === 'confirm') {
        if (this.companiesService.currentSection === Sections.Edit) {
          this.onSendCreate(result.newUser);
        } else {
          if (!this.companiesService.currentCompany.users) this.companiesService.currentCompany.users = [];
          this.companiesService.currentCompany.users.push(result.newUser);
        }
      }
      this.indexUsers();
    });
  }

  showSnackBarError = async (message: string) => {
    this.snackBar.open(
      `${this.translateService.instant('Shared.Error')}: ${message}`,
      await lastValueFrom(this.translateService.get('Shared.Close')),
      { duration: 5000 }
    );
  };

  onSendUpdate(newUser) {
    this.companiesService.loading = true;

    this.companyUserService.updateUser(newUser).subscribe({
      next: (res: any) => {
        this.companiesService.loading = false;
        this.onSuccessUpdate();
        this.companiesService.currentCompany.users = this.companiesService.currentCompany.users.map((u) => {
          if (u._id === newUser._id) return { ...u, ...newUser };
          return u;
        });
      },
      error: (error) => {
        this.companiesService.loading = false;

        if (error?.status === 401) {
          this.snackBar.open(
            this.translateService.instant('Shared.SessionExpired'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
          this.authService.logout();
        } else if (error.error?.reason) {
          this.showSnackBarError(error.error.reason);
        } else {
          this.snackBar.open(
            this.translateService.instant('Settings.Error.NoGetCompany'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
        }
      },
    });

    this.indexUsers();
  }

  onSendCreate(newUser) {
    this.companiesService.loading = true;
    this.companyUserService.createUser(newUser).subscribe({
      next: (res: any) => {
        this.companiesService.loading = false;
        this.onSuccessCreate({ ...res.user, ...newUser });
      },
      error: (error) => {
        this.companiesService.loading = false;
        if (error?.status === 401) {
          this.snackBar.open(
            this.translateService.instant('Shared.SessionExpired'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
          this.authService.logout();
        } else if (error.error?.reason) {
          this.showSnackBarError(error.error.reason);
        } else {
          this.snackBar.open(
            this.translateService.instant('Settings.Error.NoGetCompany'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
        }
      },
    });
  }

  onSuccessUpdate() {
    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        title: 'Settings.UpdateUserSuccess',
        description: this.translateService.instant('Settings.UpdateUserSuccess_desc'),
        rightButton: {
          title: 'OK',
          color: {
            background: '#FFCC00',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {});
  }

  onSuccessCreate(newUser) {
    this.companiesService.currentCompany.users.push(newUser);
    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        image: {
          svg: 'assets/icons/mail-check.svg',
        },
        title: 'Settings.CreateUserSuccess',
        description: this.translateService
          .instant('Settings.CreateUserSuccess_desc')
          .replace('{{email}}', newUser?.email || 'example@deitres.com'),
        rightButton: {
          title: 'OK',
          color: {
            background: '#FFCC00',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {});
  }

  allowed(user) {
    return this.companiesService.enableEditPermission(user, this.companiesService.currentCompany, true);
  }

  getButtonClass(user) {
    if (this.companiesService.enableEditPermission(user, this.companiesService.currentCompany, true))
      return { button: true };
    return { 'disable-button': true };
  }

  getUsers() {
    this.indexUsers();

    return this.companiesService.currentCompany.users || [];
  }

  editUser(user) {
    this.indexUsers();

    if (!this.companiesService.enableEditPermission(user, this.companiesService.currentCompany, true)) return;
    const dialogRef = this.dialog.open(CreateUserComponent, {
      width: '40%',
      minWidth: '550px',
      maxWidth: '600px',
      data: {
        editUser: user,
        index: user.index,
        users: this.companiesService.currentCompany.users,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: { status: string; editUser: User }) => {
      if (result?.status === 'confirm') {
        if (this.companiesService.currentSection === Sections.Edit) {
          if (result.editUser && result.editUser._id) {
            this.onSendUpdate(result.editUser);
          }
        } else if (this.companiesService.currentCompany.users) {
          this.companiesService.currentCompany.users = this.companiesService.currentCompany.users.map((u) => {
            if (u?.index === result.editUser?.index) return { ...u, ...result.editUser };
            return u;
          });
        } else {
          this.companiesService.currentCompany.users = [];
          this.companiesService.currentCompany.users.push(result.editUser);
        }
      }
      this.indexUsers();
    });
  }

  deleteUser(user) {
    if (!this.companiesService.enableEditPermission(user, this.companiesService.currentCompany, true)) return;

    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        title: 'Settings.ExcludeUserRequest',
        description: this.translateService.instant('Settings.ExcludeUserRequest_desc').replace('{{email}}', user.email),
        rightButton: {
          title: 'Settings.ExcludeUserRequest_confirm',
          color: {
            background: 'red',
            font: 'white',
          },
        },
        leftButton: {
          title: 'Shared.Cancel',
          color: {
            background: 'white',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {
      if (result?.status === 'right') {
        if (this.companiesService.currentSection === Sections.Edit) {
          this.onSendDelete(user._id);
        } else if (this.companiesService.currentCompany.users) {
          this.companiesService.currentCompany.users = this.companiesService.currentCompany.users.filter(
            (u) => u.email != user.email
          );
        }
      }
    });

    this.indexUsers();
  }

  onSendDelete(userID) {
    this.companiesService.loading = true;
    this.companyUserService.deleteUser(userID).subscribe({
      next: (res: any) => {
        this.companiesService.loading = false;
        this.companiesService.currentCompany.users = this.companiesService.currentCompany.users.filter(
          (u) => u._id !== userID
        );
        this.onSuccessDelete();
      },
      error: (error) => {
        this.companiesService.loading = false;
        if (error?.status === 401) {
          this.snackBar.open(
            this.translateService.instant('Shared.SessionExpired'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
          this.authService.logout();
        } else if (error.error?.reason) {
          this.showSnackBarError(error.error.reason);
        } else {
          this.snackBar.open(
            this.translateService.instant('Settings.Error.NoGetCompany'),
            this.translateService.instant('Shared.Close'),

            { duration: constants.snackBarErrorDuration }
          );
        }
      },
    });

    this.indexUsers();
  }

  onSuccessDelete() {
    const dialogRef = this.dialog.open(ConfirmActionDialogNewComponent, {
      width: '400px',
      data: {
        title: 'Settings.ExcludeUserSuccess',
        rightButton: {
          title: 'Ok',
          color: {
            background: '#FFCC00',
            font: 'black',
          },
        },
      },
    });

    dialogRef.afterClosed().subscribe((result: { status: string; topics: any }) => {});
  }

  isLast(item, array) {
    return array[array.length - 1] === item;
  }

  disableCreateUser() {
    return !this.companiesService.currentCompany?.receivers?.length;
  }

  convertToName(ip: string) {
    const serverLocation = this.receiversService.receiverServers.find((location) => location.ip === ip);

    if (!serverLocation) return '-';

    return serverLocation.name + ' - ' + serverLocation.location;
  }

  userAddNewUsers() {
    if (this.isEdit) return true;

    return this.companiesService.currentCompany.users?.length;
  }
}
